import { ActionName } from '@infotech-soe/metaschema';

import IApiProvider, {
  TActionArgsValues,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { CategoryOpenMode } from '../../../models/category/category';
import ContextService from '../../../services/context/ContextService';
import IContextService from '../../../services/context/IContextService';
import IUUIDService from '../../../services/uid/IUUIDService';
import UniqueId from '../../../services/uid/UniqueId';
import UUIDService from '../../../services/uid/UUIDService';
import { AppState } from '../../reducers/root';
import {
  getBalanceByDatasetId,
  getBalanceContentTab,
  getBalanceStateEntity,
} from '../../selectors/balance';
import { getCategoryState } from '../../selectors/category';
import { getBalanceEntityId } from '../../selectors/content';
import { getDatasetCategoryId } from '../../selectors/dataset';
import balance, {
  BalanceAction,
  BalanceCategory,
  BalanceDisplayMode,
  BalanceIndexSubValuesCategory,
  BalanceIndexValuesCategory,
} from '../../slices/balance';
import category from '../../slices/category';
import commands from '../../slices/commands';
import content, { ContentEntityType } from '../../slices/content';
import { DictionaryEffectType } from '../../slices/dictionary';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const balanceListener = createAppListener();

const apiProvider: IApiProvider = new MerpsApiProvider();
const idService: IUUIDService = new UUIDService();
const contextService: IContextService = new ContextService();

const execute = async (
  state: AppState,
  action: ActionName,
  args: TActionArgsValues,
  datasetId: UniqueId
): Promise<any> => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);
  const datasetsState = state.dataset;

  const context = await contextService.getContext(
    categoryState,
    datasetsState,
    datasetId
  );

  return apiProvider.actions.executeAction(
    action,
    context.toServerContext(),
    args
  );
};

balanceListener.startListening(
  apiCall(
    {
      request: balance.open,
      success: balance.openSuccess,
      failure: balance.openFailure,
    },
    async (
      {
        datasetId,
        streamId,
        templateId,
        reportId,
        subReportId,
        formName,
        title,
        indexes,
        records,
        values,
        options,
      },
      state,
      dispatch
    ) => {
      const id = idService.generateId();

      dispatch(
        category.openDisplayMode({
          category: BalanceCategory,
          displayMode: BalanceDisplayMode,
          ui: {
            mode: CategoryOpenMode.BALANCE,
            category: state.dataset[datasetId].source.category,
            balanceId: id,
          },
          inParams: { reportId, subReportId },
        })
      );

      return {
        id,
        streamId,
        templateId,
        reportId,
        subReportId,
        formName,
        title,
        indexes,
        records,
        values,
        options,
      };
    }
  )
);

balanceListener.startListening(
  postDispatch(balance.openSuccess, ({ id, formName }, _state, dispatch) => {
    dispatch(
      content.addEntity({
        type: ContentEntityType.BALANCE_FORM,
        content: id,
        title: formName,
      })
    );
  })
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.initiateCommand,
      success: balance.initiateCommandSuccess,
      failure: balance.initiateCommandFailure,
    },
    async ({ id, command }, state, dispatch) => {
      const {
        isChanged,
        isCalculate,
        options: { isShowSavingDialog },
      } = getBalanceStateEntity(state, id);

      if (command === BalanceAction.CALCULATE.name && !isCalculate) {
        dispatch(balance.setCalculate({ id, isCalculate: true }));
        return;
      }
      if (
        isChanged &&
        Object.values(BalanceAction).some(
          ({ name, confirm }) => name === command && confirm
        )
      ) {
        if (isShowSavingDialog) {
          dispatch(balance.showConfirm({ id, isShow: true }));
        } else {
          dispatch(balance.executeCommand({ id, isSave: true, command }));
        }
      } else {
        dispatch(balance.executeCommand({ id, isSave: false, command }));
      }
    }
  )
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.executeCommand,
      success: balance.executeCommandSuccess,
      failure: balance.executeCommandFailure,
    },
    async ({ id, isSave, command }, state, dispatch) => {
      const {
        datasetId,
        subReportId,
        templateId,
        values: data,
        records,
      } = getBalanceStateEntity(state, id);

      if (isSave || command === BalanceAction.STORE_VALUES.name) {
        await execute(
          state,
          BalanceAction.STORE_VALUES.name,
          {
            subReportId,
            templateId,
            data,
            records,
            isCheckEmptyRecord: true,
          },
          datasetId
        );
        if (
          Object.values(BalanceAction).some(
            ({ name, needsRefresh }) => name === command && !needsRefresh
          )
        ) {
          dispatch(balance.refresh({ datasetId }));
        }
        dispatch(balance.setChanges({ id, isChanged: false }));
      }
      if (command !== BalanceAction.STORE_VALUES.name) {
        const action =
          command === BalanceAction.EXCEL_EXPORT.name
            ? commands.execute({ datasetId, command, args: { templateId } })
            : commands.initiate({ datasetId, command });
        dispatch(action);
      }
    }
  )
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.refresh,
      success: balance.refreshSuccess,
      failure: balance.refreshFailure,
    },
    async ({ datasetId }, state) => {
      const id = getBalanceByDatasetId(state, datasetId);
      if (!id) {
        throw new Error('Balance form not found');
      }

      const { subReportId, templateId } = getBalanceStateEntity(state, id);

      const { values, records } = await execute(
        state,
        BalanceAction.GET_VALUES.name,
        { subReportId, templateId },
        datasetId
      );

      return { id, values, records };
    }
  )
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.close,
      success: balance.closeSuccess,
      failure: balance.closeFailure,
    },
    async ({ id, isSave }, state, dispatch) => {
      const {
        datasetId,
        subReportId,
        templateId,
        values: data,
        records,
      } = getBalanceStateEntity(state, id);

      if (isSave) {
        await execute(
          state,
          BalanceAction.STORE_VALUES.name,
          { subReportId, templateId, data, records },
          datasetId
        );
      }

      const entityId = getBalanceEntityId(state, id);
      if (entityId) {
        dispatch(balance.setChanges({ id, isChanged: false }));
        dispatch(content.close({ id: entityId, save: false }));
      }
      return { id, datasetId };
    }
  )
);

balanceListener.startListening(
  postDispatch(balance.closeSuccess, ({ datasetId }, state, dispatch) => {
    const categoryId = getDatasetCategoryId(state, datasetId);
    dispatch(category.close({ categoryId }));
  })
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.showConsState,
      success: balance.showConsStateSuccess,
      failure: balance.showConsStateFailure,
    },
    async (
      { id, indexCode, isSection, groupCode, rowNum },
      state,
      dispatch
    ) => {
      const { datasetId, reportId, subReportId, templateId } =
        getBalanceStateEntity(state, id);

      const { whereClause, title } = await execute(
        state,
        BalanceAction.GET_CONS_STATE.name,
        { reportId, subReportId, indexCode, isSection, groupCode, rowNum },
        datasetId
      );

      const categoryName = groupCode
        ? BalanceIndexSubValuesCategory
        : BalanceIndexValuesCategory;

      const existingEntityId = getBalanceContentTab(state, categoryName, {
        indexCode,
        whereClause,
      });

      if (existingEntityId) {
        dispatch(content.activate({ id: existingEntityId }));
        return;
      }

      dispatch(
        category.openDisplayMode({
          category: categoryName,
          displayMode: 'main',
          ui: { mode: CategoryOpenMode.CONTENT },
          inParams: { indexCode, whereClause, title, templateId },
        })
      );
    }
  )
);

balanceListener.startListening(
  apiCall(
    {
      request: balance.showIndex,
      success: balance.showIndexSuccess,
      failure: balance.showIndexFailure,
    },
    async ({ indexCode }, _state, dispatch) => {
      dispatch(
        category.openDisplayMode({
          category: 'BalanceIndexes',
          displayMode: 'main',
          ui: {
            mode: CategoryOpenMode.DICTIONARY,
            multiSelect: false,
            fullHierarchy: true,
            effect: { type: DictionaryEffectType.DO_NOTHING },
          },
          // eslint-disable-next-line camelcase
          inParams: { cond_code: indexCode },
        })
      );
    }
  )
);

export default balanceListener.middleware;
